import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { EventNote, RemoveRedEyeOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import withComponentAuth from '../../hoc/withComponentAuth';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import { SmartFilter } from  '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { ListTable } from '../../components/molecules/ListTable';
import { SecondaryButton } from '../../components/atoms/Button';
import useSnackbar from '../../hooks/useSnackbar';
import { capitalizeWords, debounce, formatDateOnly } from '../../utils/helper';
import {
  GetLastDayAbsentAttendance,
  GetTodayPresentAttendance,
  ILastDayAbsentRecord,
  ITodayPresentAttendance,
} from '../../services/technician';
import { DetailedAttendanceCalendar } from './components/DetailedAttendanceCalendar';
import { AttendanceStatusChip } from './components/AttendanceStatusChip';
import AssignHelperModal from '../ACD_helper/AssignHelperModal';
import { UnpairedTech } from '../../services/acdHelper';
import { tabStyles } from '../Leaves/index';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

type AttendanceTab =
  | 'ALL_PRESENT'
  | 'ACD_TECHNICIAN'
  | 'ACD_HELPER'
  | 'CONTRACTOR'
  | 'LAST_DAY_ABSENT';

const getAttendanceTabFilter = (tab: AttendanceTab) => {
  switch (tab) {
    case 'ACD_TECHNICIAN':
      return {
        type: 'ACD',
        excludePosition: 'HELPER,TBA',
        kycStatus: 'VERIFIED',
        workEligible: true,
      };
    case 'ACD_HELPER':
      return { position: 'HELPER', kycStatus: 'VERIFIED', workEligible: true };
    case 'CONTRACTOR':
      return {
        type: 'FC',
        excludePosition: 'TBA',
        kycStatus: 'VERIFIED',
        workEligible: true,
      };
    case 'LAST_DAY_ABSENT':
      return {
        excludePosition: 'TBA',
        kycStatus: 'VERIFIED',
        workEligible: true,
      };
    default:
      return {
        excludePosition: 'TBA',
        kycStatus: 'VERIFIED',
        workEligible: true,
      };
  }
};

const getAttendanceHeaderCells = (
  tab: AttendanceTab,
): [string, number][] => {
  if (tab === 'LAST_DAY_ABSENT') {
    return [
      ['Technician Name', 140],
      ['Last Day Absent', 120],
      ['Absence Duration', 120],
      ['Leave', 100],
      ['Present History', 110],
      ['Action', 70],
    ];
  }

  return [
    ['Technician Name', 120],
    ['Check In', 90],
    ['Check Out', 90],
    ['Status', 100],
    [tab === 'ACD_HELPER' ? 'Paired Technician' : 'Paired Helper', 110],
    ['Pair', 100],
    ['All Present', 80],
    ['Action', 70],
  ];
};

const TechnicianAttendance = () => {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const { canEdit } = useAdminPermission();
  const canEditHelperPairing = canEdit('technician_pairing');

  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    searchParams,
    setSearchParams,
  } = usePaginationParams();
  const [technicians, setTechnicians] = useState<
    Array<ITodayPresentAttendance | ILastDayAbsentRecord>
  >([]);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [tabValue, setTabValue] = useState<AttendanceTab>(
    () => (searchParams.get('tab') as AttendanceTab) ?? 'ALL_PRESENT',
  );
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);

  const [extendedAttendance, setExtendedAttendance] = useState<{
    technicianId: string;
    technicianName: string;
    refreshKey: number;
  } | null>(null);

  const [pairModalTechnicianId, setPairModalTechnicianId] = useState<
    string | null
  >(null);

  const fetchTodayPresentAttendance = useCallback(
    async (
      pageNum: number,
      limit: number,
      search?: string,
      tab: AttendanceTab = 'ALL_PRESENT',
    ) => {
      try {
        setIsLoading(true);

        const response =
          tab === 'LAST_DAY_ABSENT'
            ? await GetLastDayAbsentAttendance({
                page: pageNum,
                limit,
                searchQuery: search,
                ...getAttendanceTabFilter(tab),
              })
            : await GetTodayPresentAttendance({
                page: pageNum,
                limit,
                searchQuery: search,
                ...getAttendanceTabFilter(tab),
              });

        setTechnicians(response?.data || []);
        setTotalTechnician(
          response?.pagination?.totalTechnicians || response?.count || 0,
        );
      } catch (error) {
        console.error('Failed to fetch attendance:', error);
        setTechnicians([]);
        setTotalTechnician(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchTodayPresentAttendance(page, rowsPerPage, searchQuery, tabValue);
  }, [page, rowsPerPage, searchQuery, tabValue, fetchTodayPresentAttendance]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleSearchRefresh = useMemo(
    () =>
      debounce((search: string) => {
        const normalized = search.trim();
        setSearchQuery(normalized || undefined);
        setPage(1);
      }, 500),
    [],
  );

  const refreshList = () => {
    fetchTodayPresentAttendance(page, rowsPerPage, searchQuery, tabValue);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: AttendanceTab) => {
    if (tabValue === newValue) return;

    setTechnicians([]);
    setTotalTechnician(0);
    setTabValue(newValue);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', newValue);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const canManagePairing = (technician: ITodayPresentAttendance) => {
    if (!canEditHelperPairing) return false;

    const position = technician.technicianPosition;
    const type = technician.technicianType;

    if (position === 'HELPER') {
      return technician.isPaired === true;
    }

    return type === 'ACD' && position !== 'HELPER';
  };

  const formatTime = (time?: string) => {
    if (!time) return 'N/A';

    const parsedTime = dayjs(time);

    if (!parsedTime.isValid()) return 'N/A';

    return parsedTime.format('hh:mm A');
  };

  const getPairedHelper = (technician: ITodayPresentAttendance) => {
    const name = technician.pairedHelperName?.trim();
    if (name) return capitalizeWords(name);

    const item = technician as ITodayPresentAttendance & {
      pairedHelper?: { name?: string } | string;
      helper?: { name?: string };
    };

    const helper = item?.pairedHelper || item?.helper;

    if (!helper) return 'N/A';

    if (typeof helper === 'string') {
      return capitalizeWords(helper);
    }

    return capitalizeWords(helper?.name as string) || 'N/A';
  };

  const getTechnicianId = (technician: { technicianId?: string }) => {
    const id = technician.technicianId;
    if (id) return String(id);
    return null;
  };

  const getPairedHelperId = (technician: ITodayPresentAttendance) => {
    if (technician.pairedHelperId) return String(technician.pairedHelperId);
    return null;
  };

  const handleViewTechnician = (technician: {
    technicianId?: string;
  }) => {
    const technicianId = getTechnicianId(technician);

    if (!technicianId) {
      console.error('Technician id not found:', technician);
      return;
    }

    navigate(`/technicians/${technicianId}`);
  };

  const openExtendedAttendance = (technician: {
    technicianId?: string;
    technicianName?: string;
  }) => {
    const technicianId = getTechnicianId(technician);

    if (!technicianId) {
      showSnackbar('Technician id not found', 'error');
      return;
    }

    setExtendedAttendance({
      technicianId,
      technicianName: technician.technicianName || '',
      refreshKey: Date.now(),
    });
  };

  const handlePairStatusClick = async (technician: ITodayPresentAttendance) => {
    const technicianId = getTechnicianId(technician);

    if (!technicianId) {
      showSnackbar('Technician id not found', 'error');
      return;
    }

    if (!canManagePairing(technician)) {
      return;
    }

    if (technician.isPaired) {
      const helperId = getPairedHelperId(technician);

      if (!helperId) {
        showSnackbar('Paired helper not found', 'error');
        return;
      }

      try {
        const response = await UnpairedTech(helperId);
        if (response === 200) {
          showSnackbar('Helper unpaired successfully', 'success');
          refreshList();
        } else {
          showSnackbar('Unpair failed', 'error');
        }
      } catch {
        showSnackbar('Error while unpairing helper', 'error');
      }
      return;
    }

    setPairModalTechnicianId(technicianId);
  };

  const attendanceHeaderCells = useMemo(
    () => getAttendanceHeaderCells(tabValue),
    [tabValue],
  );

  const getPairButtonStyle = (isPaired: boolean) => {
    if (isPaired) {
      return {
        statusValue: 'Unpair',
        borderColor: '#18D824',
        textColor: '#18D824',
        shadowColor: 'rgba(24, 216, 36, 0.12)',
      };
    }

    return {
      statusValue: 'Pair',
      borderColor: '#FFA726',
      textColor: '#FFA726',
      shadowColor: 'rgba(255, 167, 38, 0.12)',
    };
  };

  const getLeaveChipStyle = (leaveStatus: ILastDayAbsentRecord['leaveStatus']) => {
    switch (leaveStatus) {
      case 'APPROVED':
        return { label: 'Approved', color: '#18D824' };
      case 'REJECTED':
        return { label: 'Rejected', color: '#E31E25' };
      case 'PENDING':
        return { label: 'Pending', color: '#FFA726' };
      default:
        return { label: 'Not Apply', color: '#8A8D91' };
    }
  };

  const formatAbsenceDuration = (days: number) =>
    `${days} Day${days === 1 ? '' : 's'}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}

      <SmartFilter
        title="Technician Attendance"
        createFilter={['SEARCH']}
        addDrawerSize="lg"
        filterdrawerSize="sm"
        filterHeader="Filter Attendance"
        addHeader="Attendance"
        onSearch={handleSearchRefresh}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
      <Box display="flex" justifyContent="center" width="100%" my={2}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={tabStyles}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="ALL_PRESENT" label="All Present" />
          <Tab value="ACD_TECHNICIAN" label="ACD Technician" />
          <Tab value="ACD_HELPER" label="ACD Helper" />
          <Tab value="CONTRACTOR" label="Contractor" />
          <Tab value="LAST_DAY_ABSENT" label="Last Day Absent" />
        </Tabs>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
      >
        <ListTable
          headerCells={attendanceHeaderCells}
          rows={technicians}
          isLoading={isLoading}
          noDataText={
            tabValue === 'LAST_DAY_ABSENT'
              ? 'No Absence Records Found'
              : 'No Present Technicians Found'
          }
          renderCell={(
            row: ITodayPresentAttendance | ILastDayAbsentRecord,
            colIndex: number,
          ) => {
            if (tabValue === 'LAST_DAY_ABSENT') {
              const record = row as ILastDayAbsentRecord;

              switch (colIndex) {
                case 0:
                  return capitalizeWords(record.technicianName) || 'N/A';

                case 1:
                  return formatDateOnly(record.lastDayAbsent);

                case 2:
                  return formatAbsenceDuration(record.absenceDuration);

                case 3: {
                  const { label, color } = getLeaveChipStyle(record.leaveStatus);
                  return (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color,
                        border: `1px solid ${color}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Box>
                  );
                }

                case 4:
                  return (
                    <IconButton
                      size="small"
                      onClick={() => openExtendedAttendance(record)}
                      aria-label="Open detailed attendance"
                      sx={{
                        color: '#E31E25',
                        '&:hover': {
                          backgroundColor: 'rgba(227, 30, 37, 0.12)',
                        },
                      }}
                    >
                      <EventNote />
                    </IconButton>
                  );

                case 5:
                  return (
                    <IconButton
                      size="small"
                      onClick={() => handleViewTechnician(record)}
                      sx={{
                        color: '#E31E25',
                        '&:hover': {
                          backgroundColor: 'rgba(227, 30, 37, 0.12)',
                        },
                      }}
                    >
                      <RemoveRedEyeOutlined />
                    </IconButton>
                  );

                default:
                  return null;
              }
            }

            const technician = row as ITodayPresentAttendance;

            switch (colIndex) {
              case 0:
                return capitalizeWords(technician.technicianName) || 'N/A';

              case 1:
                return formatTime(technician.checkInTime);

              case 2:
                return formatTime(technician.checkOutTime);

              case 3:
                return (
                  <AttendanceStatusChip
                    status={technician.computedStatus}
                    withHint
                  />
                );

              case 4:
                return getPairedHelper(technician);

              case 5: {
                const { statusValue, borderColor, textColor, shadowColor } =
                  getPairButtonStyle(Boolean(technician.isPaired));

                const pairingEnabled = canManagePairing(technician);

                const pairButton = (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!pairingEnabled}
                    onClick={() => handlePairStatusClick(technician)}
                    sx={{
                      minWidth: 96,
                      px: 2.2,
                      py: 0.75,
                      border: `1px solid ${borderColor}`,
                      borderRadius: '8px',
                      color: textColor,
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: 1,
                      backgroundColor: 'transparent',
                      boxShadow: `0 0 8px ${shadowColor}`,
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      opacity: pairingEnabled ? 1 : 0.7,
                      '&:hover': pairingEnabled
                        ? {
                            backgroundColor: technician.isPaired
                              ? 'rgba(24, 216, 36, 0.08)'
                              : 'rgba(232, 91, 91, 0.08)',
                            borderColor,
                          }
                        : undefined,
                    }}
                  >
                    {statusValue}
                  </Button>
                );

                return pairButton;
              }

              case 6:
                return (
                  <IconButton
                    size="small"
                    onClick={() => openExtendedAttendance(technician)}
                    aria-label="Open detailed attendance"
                    sx={{
                      color: '#E31E25',
                      '&:hover': {
                        backgroundColor: 'rgba(227, 30, 37, 0.12)',
                      },
                    }}
                  >
                    <EventNote />
                  </IconButton>
                );

              case 7:
                return (
                  <IconButton
                    size="small"
                    onClick={() => handleViewTechnician(technician)}
                    sx={{
                      color: '#E31E25',
                      '&:hover': {
                        backgroundColor: 'rgba(227, 30, 37, 0.12)',
                      },
                    }}
                  >
                    <RemoveRedEyeOutlined />
                  </IconButton>
                );

              default:
                return null;
            }
          }}
        />
      </Box>

      <Box display="block" bottom={0} right={0}>
        <Pagination
          count={totalTechnician}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      <Dialog
        open={Boolean(extendedAttendance)}
        onClose={() => setExtendedAttendance(null)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: '#111',
            color: '#fff',
            width: '96vw',
            maxWidth: 1400,
            maxHeight: '92vh',
            borderRadius: 2,
            border: '1px solid #333',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: '1px solid #2a2a2a',
            backgroundColor: '#111',
          }}
        >
          Technician Detailed Attendance —{' '}
          {capitalizeWords(extendedAttendance?.technicianName || '')}
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: '#111', p: 2 }}>
          {extendedAttendance && (
            <DetailedAttendanceCalendar
              key={extendedAttendance.refreshKey}
              technicianId={extendedAttendance.technicianId}
              onSuccess={(message) => showSnackbar(message, 'success')}
              onError={(message) => showSnackbar(message, 'error')}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{ backgroundColor: '#111', borderTop: '1px solid #2a2a2a', p: 2 }}
        >
          <SecondaryButton
            title="Close"
            onClick={() => setExtendedAttendance(null)}
          />
        </DialogActions>
      </Dialog>

      <AssignHelperModal
        isOpen={Boolean(pairModalTechnicianId)}
        onClose={() => setPairModalTechnicianId(null)}
        bookingId={pairModalTechnicianId || ''}
        onReload={refreshList}
      />
      </Box>
    </Box>
  );
};
export default withComponentAuth(TechnicianAttendance);
